import { useEffect, useState } from "react";

import { OverridableStringUnion } from "@mui/types";
import { ChipPropsColorOverrides, Box, Chip } from "@mui/material";

import { DateTime } from "luxon";

import { ZonedTimeRange } from "types/ZonedTimeRange";
import { luxonUtils } from "utils/luxonUtils";

type StaffingState = "staffed" | "unstaffed" | "unknown";

export interface SiteStaffingChipProps {
    staffingTimes?: ZonedTimeRange[];
}

export function SiteStaffingChip(props: SiteStaffingChipProps) {
    const [staffingState, setStaffingState] = useState<
        StaffingState | undefined
    >();

    useEffect(() => {
        const determineStaffedState = () => {
            let curState: StaffingState = "unknown";

            if (props.staffingTimes) {
                const isCurrentlyStaffed = props.staffingTimes.some(
                    (schedule) => {
                        const startTime = luxonUtils.fromNodaTimeZonedDateTime(
                            schedule.start,
                        );
                        const endTime = luxonUtils.fromNodaTimeZonedDateTime(
                            schedule.end,
                        );

                        if (startTime.isValid && endTime.isValid) {
                            const now = DateTime.now();
                            return now >= startTime && now < endTime;
                        }
                        return false;
                    },
                );

                curState = isCurrentlyStaffed ? "staffed" : "unstaffed";
            }

            setStaffingState(curState);
        };

        // on load component
        determineStaffedState();

        // refresh every X seconds
        const intervalInSeconds = 30;
        const interval = setInterval(
            determineStaffedState,
            1000 * intervalInSeconds,
        );

        return () => clearInterval(interval);
    }, [props.staffingTimes]);

    let color: OverridableStringUnion<
        | "default"
        | "primary"
        | "secondary"
        | "error"
        | "info"
        | "success"
        | "warning",
        ChipPropsColorOverrides
    > = "default";

    switch (staffingState) {
        case "staffed":
            color = "success";
            break;

        case "unstaffed":
            color = "error";
            break;
    }

    return (
        <Box
            sx={{
                flexGrow: 0,
                flexShrink: 0,
                alignContent: "center",
            }}
        >
            {staffingState && (
                <Chip
                    color={color}
                    sx={{
                        fontWeight: "bold",
                        span: { letterSpacing: "0.3em", marginRight: "-.3em" },
                        textTransform: "uppercase",
                    }}
                    label={staffingState}
                    size="medium"
                />
            )}
        </Box>
    );
}
